/**
 * Normalize `userData.Modules` from login / org-switch API responses.
 * Handles PascalCase/camelCase keys and legacy DataTable JSON shapes.
 */

function pickRowField(row, ...keys) {
  if (!row || typeof row !== "object") return "";
  for (const key of keys) {
    if (row[key] != null && String(row[key]).trim() !== "") {
      return String(row[key]).trim();
    }
  }
  return "";
}

/** @returns {{ ModuleId?: string, ModuleName: string, Indexnumber?: number } | null} */
export function normalizeLoginModuleRow(row) {
  if (row == null) return null;

  if (Array.isArray(row)) {
    const [moduleId, moduleName, indexNumber] = row;
    const name = String(moduleName ?? "").trim();
    if (!name) return null;
    return {
      ModuleId: moduleId != null ? String(moduleId) : undefined,
      ModuleName: name,
      Indexnumber:
        indexNumber != null && indexNumber !== "" ? Number(indexNumber) : undefined,
    };
  }

  const moduleName = pickRowField(
    row,
    "ModuleName",
    "moduleName",
    "MODULENAME",
    "Name",
    "name",
  );
  if (!moduleName) return null;

  const moduleId = pickRowField(row, "ModuleId", "moduleId", "MODULEID");
  const indexRaw = pickRowField(
    row,
    "Indexnumber",
    "IndexNumber",
    "indexnumber",
    "indexNumber",
  );

  return {
    ModuleId: moduleId || undefined,
    ModuleName: moduleName,
    Indexnumber: indexRaw !== "" ? Number(indexRaw) : undefined,
  };
}

/** Resolve organisation id from login session (API uses mixed casing). */
export function getSessionOrganizationId(userData) {
  if (!userData) return "";
  const id =
    userData.OrganizationId ??
    userData.Organizationid ??
    userData.organizationId ??
    userData.organizationid;
  return id != null ? String(id).trim() : "";
}

function parseModulesRaw(raw) {
  if (raw === undefined) return undefined;
  if (raw == null) return [];

  if (Array.isArray(raw)) {
    return raw.map(normalizeLoginModuleRow).filter(Boolean);
  }

  if (typeof raw === "object") {
    const rows = raw.Rows ?? raw.rows;
    if (Array.isArray(rows)) {
      const columns = (raw.Columns ?? raw.columns ?? [])
        .map((col) =>
          typeof col === "string"
            ? col
            : col?.ColumnName ?? col?.columnName ?? "",
        )
        .filter(Boolean);

      if (columns.length > 0 && rows.length > 0 && Array.isArray(rows[0])) {
        return rows
          .map((values) => {
            const obj = {};
            columns.forEach((col, idx) => {
              obj[col] = values[idx];
            });
            return normalizeLoginModuleRow(obj);
          })
          .filter(Boolean);
      }

      return rows.map(normalizeLoginModuleRow).filter(Boolean);
    }
  }

  return [];
}

/**
 * @returns {Array<{ ModuleId?: string, ModuleName: string, Indexnumber?: number }> | null}
 *   null = Modules property absent on session (treat as no licensed modules).
 *   []   = org has no licensed modules in session.
 */
export function getLoginModuleRows(userData) {
  if (!getSessionOrganizationId(userData)) return null;

  const raw =
    userData.Modules !== undefined
      ? userData.Modules
      : userData.modules !== undefined
        ? userData.modules
        : userData.OrgnanizationModules !== undefined
          ? userData.OrgnanizationModules
          : userData.orgnanizationModules !== undefined
            ? userData.orgnanizationModules
            : undefined;

  if (raw === undefined) return null;
  return parseModulesRaw(raw);
}

/** Normalize Modules on session to a consistent array after login / org switch. */
export function normalizeUserSessionModules(userData) {
  if (!userData || typeof userData !== "object") return userData;
  const rows = getLoginModuleRows(userData);
  if (rows !== null) {
    userData.Modules = rows;
  }
  return userData;
}

export function loginSessionHasModulesProperty(userData) {
  if (!userData) return false;
  return (
    Object.prototype.hasOwnProperty.call(userData, "Modules") ||
    Object.prototype.hasOwnProperty.call(userData, "modules")
  );
}
